import { captureException } from '@sentry/nextjs'
import { useRouter } from 'next/router'
import { useDispatch } from 'react-redux'
import { FEED_RESET } from '../redux/constants'
import { scrubItemAssets } from '../redux/designerReducers/assetsReducer'
import { capture, clearAll, initialize, setSelectedBubble } from '../redux/designerReducers/canvasReducer'
import { setShowMembershipModal } from '../redux/designerReducers/designerReducer'
import { fetchDesignGames } from '../redux/designerReducers/designGamesReducer'
import { post, saveDraft } from '../redux/designerReducers/designsReducer'
import { clearFilters } from '../redux/designerReducers/itemsReducer'
import { setLocationState } from '../redux/designerReducers/locationStateReducer'
import { fetchFeed, setAttr } from '../redux/feedInfiniteSlice'
import { logEvent } from '../utils/firebase'
import { sleep } from '../utils/sleep'

export const usePostAIDesign = ({ setIsSending, onClose, member, postText = '', selectedFilter = null, aiImageUrl = null }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const postAIDesign = async (redirectParam = '') => {
    try {
      setIsSending(true)
      await sleep(100)

      // First, set up a minimal canvas state for AI design
      const aiCanvasData = {
        template: aiImageUrl,
        targets: [], // AI designs have no items
        width: 750,
        height: 750,
        designData: {
          className: 'AIDesign',
          tags: ['ai-generated']
        }
      }

      // Initialize canvas with AI design data
      dispatch(initialize(aiCanvasData))
      // Set the AI image as the captured design
      dispatch(capture(aiImageUrl))

      await sleep(100)

      // Save as draft first to get an objectId
      const saveResult = await dispatch(saveDraft())
      const objectId = saveResult.payload.result.idea.objectId

      // Create post data with objectId from saved draft
      const postData = {
        postText,
        objectId,
        type: 'ai-design'
      }

      if (selectedFilter) {
        const selectedFilterImg = selectedFilter ? scrubItemAssets(selectedFilter)[0]?.original : null

        postData.animationInfo = {
          objectId: selectedFilter.objectId,
          animationUrl: selectedFilterImg,
          title: selectedFilter.title,
          numUses: selectedFilter.numUses
        }
      }

      const r = await dispatch(post(postData))

      dispatch(
        setLocationState({
          data: {
            type: 'postSuccess',
            ...r.payload
          }
        })
      )

      const attrMyEvent = {
        addition: {
          statuses: ['active'],
          filter: ['active']
        }
      }

      await dispatch(fetchDesignGames())
      dispatch(setAttr('template')({ addition: {} }))
      dispatch(fetchFeed('template')({ status: FEED_RESET }))
      dispatch(setAttr('myEventPlayTab')(attrMyEvent))
      await dispatch(fetchFeed('myEventPlayTab')({ status: FEED_RESET }))
      await dispatch(fetchFeed('draft')({ status: FEED_RESET }))
      dispatch(fetchFeed('myroom')({ status: FEED_RESET }))
      dispatch(fetchFeed('mydecor')({ status: FEED_RESET }))

      // Redirect to success page like PostModal.js does
      router.push(`/success/${objectId}`)

      dispatch(clearFilters())
      dispatch(setSelectedBubble(null))

      if (redirectParam.includes('tutorial')) {
        dispatch(setShowMembershipModal(true))
      }

      setIsSending(false)
      logEvent('publish_design', {})
      onClose()
      dispatch(clearAll())
    } catch (error) {
      console.log(error)
      captureException(error)
      setIsSending(false)
      onClose()
      alert('Error occurred while posting AI design. Please try again.')
    }
  }

  return postAIDesign
}
